import { minutesToHours } from 'date-fns';
import { useI18n } from 'vue-i18n';
import { secondsToClosestMinutes } from '../helpers';

export function useDurationHelpers() {
    const { t } = useI18n();

    function humanizeMinutes(minutes: number) {
        return t('common.resources.minutesArray', minutes, { default: '0 mins | 1 min | {count} mins' });
    }

    function humanizeHours(hours: number) {
        return t('common.resources.hoursArray', hours, { default: '0 h | 1 h | {count} h' });
    }

    function humanizeMinutesLong(minutes: number) {
        return t('common.resources.minutesLongArray', minutes, { default: '0 mins | 1 min | {count} mins' });
    }

    function humanizeHoursLong(hours: number) {
        return t('common.resources.hoursLongArray', hours, { default: '0 hours | 1 hour | {count} hours' });
    }

    /**
     * Converts duration in seconds to humanized time string.
     * @param seconds Input value in seconds
     * @returns Humanized time string in hours and minutes
     */
    function humanizeDuration(seconds: number | undefined | null): string {
        if (typeof seconds !== 'number') return '';

        // if less than 1 minute, display as '< 1 min'
        if (seconds < 60) return `< ${humanizeMinutes(1)}`;

        const minutes = secondsToClosestMinutes(seconds);

        // if less than an hour, return just the minutes
        if (minutes < 60) return humanizeMinutes(minutes);

        // else display long hours and minutes
        const hours = minutesToHours(minutes);
        const _minutes = minutes - hours * 60;

        return `${humanizeHours(hours)}${_minutes > 0 ? ', ' + humanizeMinutes(_minutes) : ''}`;
    }

    /**
     * Converts duration in seconds to humanized long time string.
     * @param seconds Input value in seconds
     * @returns Humanized time string in hours and minutes
     */
    function humanizeDurationLong(seconds: number | undefined | null): string {
        if (typeof seconds !== 'number') return '';

        // if less than 1 minute, display as '< 1 min'
        if (seconds < 60) return `< ${humanizeMinutesLong(1)}`;

        const minutes = Math.ceil(seconds / 60);

        // if less than an hour, return just the minutes
        if (minutes < 60) return humanizeMinutesLong(minutes);

        // else display long hours and minutes
        const hours = Math.floor(minutes / 60);
        const _minutes = minutes - hours * 60;

        return `${humanizeHoursLong(hours)}${_minutes > 0 ? ', ' + humanizeMinutesLong(_minutes) : ''}`;
    }

    return {
        humanizeDuration,
        humanizeDurationLong
    };
}
